<template>
  <!-- Header -->
  <div class="relative md:pt-4 pb-4 pt-2">
    <div class="px-4 md:px-4 mx-auto w-full" v-if="has('plan.summary.view')">
      <div>
        <!-- Card stats -->
        <div class="flex flex-wrap">
          <div v-for="(stat, i) in Object.values(planSummary())" :key="i" class="w-full lg:w-6/12 xl:w-3/12 p-2">
            <card-stats
              :statSubtitle="`${(stat?.status?.toLowerCase()==='retry'? 'DUE': stat.status)} ${params?.type?.toUpperCase() || 'plan'}`"
              :statTitle="formatter().format(stat.amount || 0)"
              :statArrow="`${'up'}`"
              :statCount="`${stat.total}`"
              :statPercentColor="`text-orange-500`"
              :statDescripiron="`${stat?.status?.toLowerCase()==='retry'? 'due': stat?.status?.toLowerCase()} ${params.type || 'plan'} count`"
              statIconName="far fa-chart-bar"
              statIconColor="bg-blueGray-900"
            />
          </div>
        </div>
      </div>

    </div>

    <!-- Filter/Download/date-preset triggers now live on the Plan Subscription List
         table header (CardUserPlanList.vue) instead of duplicating controls here —
         this component just exposes filter()/applyDatePreset()/downloadSummary()/
         toggleFilterModal() for Plans.vue to call via a $ref on this component. -->
    <filter-modal
      :selects="{
        type: ['loan', 'savings', 'investment'],
        plan: planOptions,
        duration: ['24hrs', '1week', '30days', '3months', '6months', '1year'].map( t => ({key: t, value: `${t} Ago`})),
        status: ['INITIATED', 'PENDING', 'FAILED', 'ACTIVE', 'DECLINED', 'RETRY', 'COMPLETED', 'WITHDRAWN', 'APPROVED', 'DUE', 'DEFAULT', 'BAD']
      }"
      :dates="{
        from: 'Start Date',
        to: 'End Date'
      }"
      :searchable="true"
      :sizable="true"
      :visible="modals.filter"
      @close="modals.filter = false"
      :title="params.type || 'plan'"
      @filter="filter"
      :loading="loading || loaders().fetchSummary || loaders().getDashboard"
    ></filter-modal>
  </div>
</template>

<script>
import CardStats from "@/components/Cards/CardStats.vue";
import { mapActions, mapGetters } from 'vuex';
import moment from 'moment'
import FilterModal from "@/components/Modals/FilterModal.vue";

export default {
  components: {
    CardStats,
    FilterModal
  },
  created() {
  },
  mounted() {
    this.getPlanSummary({})
  },
  data() {
    return {
      params: {},
      modals: {},
      businessOptions: [],
      planOptions: []
    }
  },
  methods: {
    ...mapActions(["getPlanSummary"]),
    ...mapGetters(["planSummary", "plans", "businesses", "userPlanDefaultStatSummary", "loaders"]),
    // Create our number formatter.
    formatter() {
      return new Intl.NumberFormat('en-NG', {
        style: 'currency',
        currency: 'NGN',

        // These options are needed to round to whole numbers if that's what you want.
        //minimumFractionDigits: 0, // (this suffices for whole numbers, but will print 2500.10 as $2,500.1)
        //maximumFractionDigits: 0, // (causes 2500.99 to be printed as $2,501)
      });
    },
    paramsToQuery(param = null) {
      return '?' + Object.entries(param || this.params).filter(([,v]) => v && v !== "").map(([k, v]) => `${k}=${v}`).join("&")
    },
    fetchFilteredSummary() {
      this.params = {
        ...this.params
      }
      this.$emit('statChange', this.params)
      this.getPlanSummary({q: this.paramsToQuery(this.params)});
    },
    parseDuration(duration) {
      let to = null
      let from = null
      const value = duration;
      switch (value) {
        case "24hrs":
          to = moment(new Date).format("YYYY-MM-DD")
          from = moment(new Date).subtract(1, 'day').format("YYYY-MM-DD")
          break;
        case "1week":
          to = moment(new Date).format("YYYY-MM-DD")
          from = moment(new Date).subtract(1, 'week').format("YYYY-MM-DD")
          break;
        case "30days":
          to = moment(new Date).format("YYYY-MM-DD")
          from = moment(new Date).subtract(1, 'month').format("YYYY-MM-DD")
          break;
        case "3months":
          to = moment(new Date).format("YYYY-MM-DD")
          from = moment(new Date).subtract(3, 'month').format("YYYY-MM-DD")
          break;
        case "6months":
          to = moment(new Date).format("YYYY-MM-DD")
          from = moment(new Date).subtract(6, 'month').format("YYYY-MM-DD")
          break;
        case "1year":
          to = moment(new Date).format("YYYY-MM-DD")
          from = moment(new Date).subtract(1, 'year').format("YYYY-MM-DD")
          break;  
        default:
          to = moment(new Date).format("YYYY-MM-DD")
          from = moment(new Date).subtract(5, 'year').format("YYYY-MM-DD")
          break;
      }
      this.params = {...this.params, to, from}
    },
    filter(p={}) {
      if (p.duration) {
        this.parseDuration(p.duration)
      }
      // The filter modal exposes this as a "plan" select (all available plans, so
      // the user can filter the subscription list down to one specific plan) but
      // the backend only understands the plan-name match as `category` (or `name`)
      // — translate here so the modal/UI can use the clearer field name without a
      // backend change.
      if (p.plan !== undefined) {
        p = { ...p, category: p.plan }
        delete p.plan
      }
      // Newly-submitted values must win over whatever's already in this.params —
      // it was spread first before, which meant a stale from/to could never be
      // overridden by a fresh manual date-range or preset selection.
      this.params = {
        ...this.params,
        ...p,
      }
      this.$emit('statChange', this.params)
      this.getPlanSummary({q: this.paramsToQuery(this.params)})
    },
    // Today/7D/30D shortcuts — reuses the exact same filter() path the modal's own
    // date pickers go through, so this stays consistent with manual date filtering
    // instead of being a parallel, easily-diverging mechanism. Called externally via
    // a $ref from Plans.vue, since the preset buttons themselves live on the Plan
    // Subscription List table header now.
    applyDatePreset(preset) {
      const toDate = new Date();
      const fromDate = new Date();
      fromDate.setDate(toDate.getDate() - (preset.days - 1));
      const toIso = d => d.toISOString().slice(0, 10);
      this.filter({ from: toIso(fromDate), to: toIso(toDate) });
    },
    downloadSummary() {
      this.$emit('statChange', {...this.params, get_csv: true})
    },
    toggleFilterModal() {
      this.setBusinesses()
      this.setPlans()
      this.modals.filter = !this.modals.filter
    },
    setBusinesses: function() {
      this.businessOptions = this.businesses()?.data?.map( b => ({key: b.id, value: `${b.name} | ${b.code}` }))
    },
    setPlans: function() {
      // All available plans, regardless of type — this used to be restricted to
      // plans matching an already-selected `type`, which meant the dropdown came
      // up empty until a type filter was applied first.
      this.planOptions = [...new Set(this.plans().map(p => p.name))]
    }
  }
};
</script>
